import { Box, Button, Card, CardContent, Divider, Stack, Typography } from '@mui/material';
import { KeyboardEvent } from 'react';

import { Offer } from '../../../../domain/qualification';
import { formatCentsCurrencyWhole } from '../../../../general/utils/formatters';

type Props = {
  offer: Offer;
  index: number;
  onSelect: (offerId: string) => void;
  hideFeeAndRebate?: boolean;
  clickable?: boolean;
  selected?: boolean;
};

const calculateBillingPeriod = (billingCycle: number): string => {
  switch (billingCycle) {
    case 7:
      return 'Weekly';
    case 14:
      return 'Biweekly';
    case 30:
    default:
      return 'Monthly';
  }
};

const formatFuelRebate = (hundredthsOfCent: number): string => {
  const cents = Number((hundredthsOfCent / 100).toFixed(2));
  return `${cents}¢ Fuel`;
};

const formatNonFuelRebate = (basisPoints: number): string => {
  const percent = Number((basisPoints / 100).toFixed(2));
  return `${percent}% Non-fuel`;
};

type FieldProps = { label: string; value: string };

const Field = ({ label, value }: FieldProps) => (
  <Box>
    <Typography variant="body2" color="textSecondary">
      {label}
    </Typography>
    <Typography variant="subtitle1-medium">{value}</Typography>
  </Box>
);

const OfferCard = ({
  offer,
  index,
  onSelect,
  hideFeeAndRebate = false,
  clickable = false,
  selected = false,
}: Props) => {
  const rebate = `${formatFuelRebate(offer.fuelRebateHundredthsOfCent)} · ${formatNonFuelRebate(offer.nonFuelRebateBasisPoints)}`;

  const handleSelect = () => onSelect(offer.id);

  const clickableSx = clickable
    ? {
        cursor: 'pointer',
        my: selected ? 0 : 1,
      }
    : {};

  return (
    <Card
      variant={clickable && selected ? 'coolTonedGradient' : 'outlined'}
      sx={{ width: 303, flexShrink: 0, display: 'flex', borderRadius: 2, ...clickableSx }}
      {...(clickable && {
        role: 'button',
        tabIndex: 0,
        'aria-pressed': selected,
        onClick: handleSelect,
        onKeyDown: (e: KeyboardEvent) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            handleSelect();
          }
        },
      })}
    >
      <CardContent
        sx={{
          display: 'flex',
          flexDirection: 'column',
          flex: 1,
          px: 3,
          pt: clickable && selected ? 4 : 3,
          '&:last-child': { pb: clickable && selected ? 4 : 3 },
        }}
      >
        <Typography variant="h5-bold" color={clickable && selected ? 'coolTonedGradient' : undefined} gutterBottom>
          Plan {index + 1}
        </Typography>
        <Divider sx={{ mb: 2 }} />
        <Stack spacing={2}>
          {!hideFeeAndRebate && <Field label="Rebate" value={rebate} />}
          {!hideFeeAndRebate && <Field label="Fee" value={`${formatCentsCurrencyWhole(offer.userFee)} User Fee`} />}
          <Field label="Payment due" value={calculateBillingPeriod(offer.billingCycle)} />
          <Field
            label="Paid within"
            value={`${offer.gracePeriodDays} ${offer.gracePeriodDays === 1 ? 'day' : 'days'} of statement end date`}
          />
        </Stack>
        {!clickable && (
          <Box mt="auto" pt={4}>
            <Button variant="contained" color="primary" fullWidth onClick={handleSelect}>
              Select plan
            </Button>
          </Box>
        )}
      </CardContent>
    </Card>
  );
};

export { OfferCard };
